import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { ContentType } from '../story/models/content-type.enum';
import { SetIncomingStory, LoadStory, ReorderLoadChunks } from '../story/state/story.actions';
import { EngineState } from '../engine/state/engine.state';
import { StoryState } from '../story/state/story.state';
import { combineLatest } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { SelectChapter } from '../story/state/chapters/story.chapters.actions';
import { EngineNodesState } from '../engine/state/nodes/engine.nodes.state';
import { ToolEnum } from '../engine/models/tool.enum';
import {
  SetActiveTool,
  ToggleAnimation,
  ToggleLabels,
  FlyToObject,
  SetGraph,
  Fade,
  ExpandViewTreeNode,
  QueryNodeInfo,
  PlayAudio,
  PlayChapterFromStart} from '../engine/state/engine.actions';
import { ViewState, ViewStateModel } from './state/view.state';
import { ViewUIStatus } from './models/view-ui-status.enum';
import { ViewActions } from './state/view.actions';
import { ViewTreeNode } from '../engine/models/view.tree-node';
import { PaintPresetFade } from '../engine/models/paint-presets.const';
import { SelectLabel, RemoveContextLabel } from '../story/state/labels/story.labels.actions';
import { StoryChaptersState } from '../story/state/chapters/story.chapters.state';
import { ChapterModel } from '../story/models/chapter.model';
import { SetCamera } from '../engine/state/camera/engine.camera.actions';
import { Location } from '@angular/common';
import { LabelModel } from '../story/models/labels/label.model';
import { ViewUIModel } from './models/view-ui.model';
import { ViewMode } from './models/view-mode.enum';
import { normalizeQueryIdParam, normalizeQueryTypeParam } from '../widget-config/initial-config';
import { BdGraph } from 'src/lib/graph/graph.model';
import { EngineService } from '../engine/engine.service';
import { TransitionModel } from '../story/models/transition.model';
import { TransitionService } from '../engine/transition.service';
import { CameraStrategy } from '../story/models/camera-strategy.enum';
import { BreakpointState } from '../breakpoint/state/breakpoint.state';
import { Breakpoint } from '../breakpoint/models/breakpoint.enum';
import { ViewWindow } from './models/view-window.enum';
import { NodeActions } from '../engine/state/nodes/engine.nodes.actions';
import { UndoActions } from '../undo/state/undo.state.actions';
import { HelpTypeEnum } from './models/help-type.enum';
import { ViewUIService } from './view-ui.service';
import { SelectBookmark } from '../bookmarks/state/bookmarks.actions';
import { environment } from 'src/environments/environment';
import { EnvironmentApplication } from 'src/environments/models/environment-application';
import { LayoutState } from '../layout/state/layout.state';
import { SiteHeaderType } from '../layout/models/site-header-type.enum';
import { UIConfig, WidgetWiring } from '../shared/widget-wiring';
import { QuizActions } from '../quiz/state/quiz.actions';
import { PendoEvents, PendoService } from '../shared/pendo.service';
import { QuizLifecycleState } from '../quiz/models/quiz-lifecycle.enum';
import { QuizState } from '../quiz/state/quiz.state';
import { TreeSearchDisplayMode } from '../shared/components/tree/tree-search/tree-search.component';

@Injectable({
  providedIn: 'root'
})
export class ViewService {

  constructor(
    private store: Store,
    private location: Location,
    private transitionService: TransitionService,
    private engineService: EngineService,
    private layoutService: ViewUIService,
    private pendoService: PendoService
  ) { }

  // ---------- Story Handlers ----------
  initStory(id: string, type: ContentType, lang?: string) {

    // Initialize state for new story
    this.store.dispatch(new SetIncomingStory(id, type, true));

    // Internal applications need additional bookmark data for the actions menu
    // Viewer does not have the actions menu
    if (environment.application !== EnvironmentApplication.Viewer && id !== 'preview') {
      this.store.dispatch(new SelectBookmark(id, type));
    }

    // Set up listeners for when the story is loaded
    const engineReady$ = this.store.select(EngineState.getReady);
    const incoming$ = this.store.select(StoryState.getIncomingStoryInfo);

    const preview = id === 'preview' ? true : false;

    combineLatest([engineReady$, incoming$]).pipe(
      filter(([ready, incoming]) => ready && !!incoming.id),
      take(1),
    ).subscribe(() => {
      this.store.dispatch(new SelectChapter()); // null chapter state
      this.store.dispatch(new LoadStory(id, type, lang, preview));
    });
  }

  nextChapter(eventSource: string) {
    const index = this.store.selectSnapshot(StoryChaptersState.getSelectedChapterIndex);
    const chapters = this.store.selectSnapshot(StoryChaptersState.getStoryChapters);

    this.pendoService.trackOncePerSession(PendoEvents.DiscoveredChapterNav, {
      source: eventSource,
      breakpoint: this.store.selectSnapshot(BreakpointState.getBreakpointLabel)
    });

    const chapter = chapters[index + 1];
    if (chapter) {
      this.selectChapter(chapter);
    }

  }

  previousChapter(eventSource: string) {
    const index = this.store.selectSnapshot(StoryChaptersState.getSelectedChapterIndex);
    const chapters = this.store.selectSnapshot(StoryChaptersState.getStoryChapters);
    
    this.pendoService.trackOncePerSession(PendoEvents.DiscoveredChapterNav, {
      source: eventSource,
      breakpoint: this.store.selectSnapshot(BreakpointState.getBreakpointLabel)
    });

    const chapter = chapters[index - 1];
    if (chapter) {
      this.selectChapter(chapter);
    }
  }

  async selectChapter(chapter: ChapterModel) {
    const currentChapterId = this.store.selectSnapshot(StoryChaptersState.getSelectedChapterId);
    const targetChapterId = chapter.id;
    const chapters = this.store.selectSnapshot(StoryChaptersState.getStoryChapters);
    const transition = this.transitionService.searchChapterTransitions(currentChapterId, targetChapterId, chapters);

    this.pendoService.trackOncePerSession(PendoEvents.DiscoveredChapterNav, {
      source: 'Chapter List',
      breakpoint: this.store.selectSnapshot(BreakpointState.getBreakpointLabel)
    });

    if (transition) {
      await this.chapterTransition(transition);
    }

    this.store.dispatch([
      new ReorderLoadChunks(chapter.id),
      new SelectChapter(chapter)
    ]);
  }

  private chapterTransition(transition: TransitionModel): Promise<boolean> {
    return new Promise(resolve => {
      // Camera transition
      if (transition.camera) {
        this.store.dispatch(new SetCamera(transition.camera, true, transition.duration));
      }

      // Chapter fade
      if (transition.fadeColor) {
        this.store.dispatch(new Fade(transition.fadeColor, transition.duration));
      }

      // Wait for duration before resolving. While this could be handled by SetCameraSuccess,
      // setting a timeout decouples the transition from the camera motion and allows for
      // transition effects without any camera movement.
      setTimeout(() => {
        resolve(true);
      }, transition.duration * 1000);
    });
  }

  // ---------- 3D Handlers ----------
  hideSelectedStructures() {
    const selectedNodes = this.store.selectSnapshot(EngineNodesState.getCheckedNodeIds);

    // hide nodes that are selecetd
    this.store.dispatch(new NodeActions.UpdateNodesShown(
      false,
      selectedNodes
    ));
  }



  // ---------- UI Handlers ----------
  selectTool(tool: ToolEnum) {

    // When quiz is active, always ensure the 'select' flips to the quiz button
    // so that multi-select questions continue to work even if the user opens
    // the tools panel. 
    const quizActive = this.store.selectSnapshot(QuizState.getQuizModeActive);
    if (quizActive && tool === ToolEnum.Select) {
      tool = ToolEnum.Quiz;
    };

    this.store.dispatch(new SetActiveTool(tool));
  }

  toggleColumn(show?: boolean) {
    const layout = this.store.selectSnapshot(ViewState.getUI);
    show = show == null ? !(layout.column.status === ViewUIStatus.Show) : show;
    layout.column.status = show ? ViewUIStatus.Show : ViewUIStatus.Collapse;

    this.store.dispatch(new ViewActions.SetUI(layout));
  }

  togglePresentation() {
    const isPresenting = this.store.selectSnapshot(ViewState.getPresentationActive);

    (isPresenting) ? this.stopPresentation() : this.startPresentation();
  }

  startPresentation() {
    this.store.dispatch(new ViewActions.TogglePresentation(true));
  }

  stopPresentation() {
    this.store.dispatch(new ViewActions.TogglePresentation(false));
  }

  toggleHelp(show?: boolean, type: HelpTypeEnum = HelpTypeEnum.Basics) {
    this.store.dispatch(new ViewActions.ToggleWindow(ViewWindow.Help, show));
    this.store.dispatch(new ViewActions.ToggleHelpTab(type));
  }

  toggleInteract(show?: boolean) {
    this.store.dispatch(new ViewActions.ToggleWindow(ViewWindow.Interact, show));
  }

  toggleOnDemand(show?: boolean) {
    this.store.dispatch(new ViewActions.SetOnDemand(show));
    this.store.dispatch(new ViewActions.ToggleWindow(ViewWindow.OnDemand, show));
  }

  toggleTutorial(show: boolean) {
    this.store.dispatch(new ViewActions.ToggleWindow(ViewWindow.Tutorial, show));

    if (show) {
      this.store.dispatch(new ViewActions.StartTutorial(true));
    }
  }

  toggleWhiteboard(show: boolean) {
    this.store.dispatch(new ViewActions.ToggleWindow(ViewWindow.Whiteboard, show));

    if (show) {
      // pause animation
      this.store.dispatch(new ToggleAnimation(false));
      // start whiteboarding
      this.store.dispatch(new ViewActions.SetWhiteboardEnabled(true));
    }
  }

  toggleLabels(show?: boolean) {
    this.store.dispatch(new ToggleLabels(show));
  }

  toggleSearch(mode = TreeSearchDisplayMode.List, show?: boolean) {
    const breakpoint = this.store.selectSnapshot(BreakpointState.getBreakpoint);
    const ui = this.store.selectSnapshot(ViewState.getUI);

    show = (show == null) ? !(ui.search.status === ViewUIStatus.Show) : show;

    // Switch modes
    ui.search.status = show ? ViewUIStatus.Show : ViewUIStatus.Hide;

    if (show) {
      ui.nodeInfo.status = ViewUIStatus.Hide;
    }

    this.store.dispatch(new ViewActions.SetSearchDisplayMode(mode));
    this.store.dispatch(new ViewActions.SetUI(ui));
  }

  toggleNodeInfo(show?: boolean) {
    this.toggleInfoLayout('nodeInfo', show);
  }

  toggleLabelInfo(show?: boolean) {
    this.toggleInfoLayout('labelInfo', show);
  }

  toggleInfoLayout(layoutField: 'nodeInfo' | 'labelInfo', show?: boolean) {
    const layout = this.store.selectSnapshot(ViewState.getUI);
    show = (show == null) ? layout[layoutField].status === ViewUIStatus.Hide : show;

    if (show) { // show target, hide others
      layout[layoutField].status = ViewUIStatus.Show;

      // hide others
      const infoFieldNames = ['nodeInfo', 'labelInfo'];
      infoFieldNames
        .filter((field) => field !== layoutField)
        .forEach((field) => layout[field].status = ViewUIStatus.Hide);

    } else { // hide target
      layout[layoutField].status = ViewUIStatus.Hide;
    }

    this.store.dispatch(new ViewActions.SetUI(layout));
  }



  // ---------- Interaction Handlers ----------
  delegateNodeClick(node: ViewTreeNode, event: HammerInput) {
    // Always clear selected label
    this.clearSelectedLabel();

    if (!node && !event.srcEvent.shiftKey) {
      this.clearSelectedNodes();

    } else if (event.srcEvent.shiftKey || this.store.selectSnapshot(ViewState.getMultiPickModeEnabled)) {
      this.checkNode(node);

    } else {
      const tool = this.store.selectSnapshot(EngineState.getActiveTool);

      switch (tool) {
        case ToolEnum.Hide:
          // this.hideNodes({nodes: [node], visible: false});
          this.hideNode(node);
          break;
        case ToolEnum.Select:
          this.selectNode(node);
          break;
        case ToolEnum.Isolate:
          this.isolateNode(node);
          break;
        case ToolEnum.Fade:
          this.fadeNode(node);
          break;
      }
    }
  }


  clearSelectedLabel() {
    this.store.dispatch(new SelectLabel());
  }

  clearSelectedNodes() {
    this.store.dispatch(new NodeActions.UpdateNodeSelected());
    this.store.dispatch(new NodeActions.UpdateNodesChecked(false));
  }

  checkNode(node: ViewTreeNode, checked?: boolean) {

    if (node === undefined) {
      return;
    }

    if (checked == null) {
      checked = this.store.selectSnapshot(EngineNodesState.getNodes)[node.id].checked;
    }

    const shouldCheck = !checked;

    this.store.dispatch(new NodeActions.UpdateNodesChecked(shouldCheck, [node.id]));

    this.store.dispatch(
      new NodeActions.UpdateNodeSelected(node.id)
    );
  }

  fadeNode(node: ViewTreeNode) {
    this.store.dispatch(new NodeActions.UpdateNodesPaint(
      PaintPresetFade.recipe,
      [node.id]
    ));
  }

  isolateNode(node: ViewTreeNode) {
    this.store.dispatch(new NodeActions.UpdateNodesShown(false));
    this.store.dispatch(new NodeActions.UpdateNodesShown(true, [node.id]));
  }

  selectNode(node: ViewTreeNode) {

    // only check 1 at a time


    const shouldCheck = !this.store.selectSnapshot(EngineNodesState.getNodes)[node.id].checked;
    this.store.dispatch(new NodeActions.UpdateNodesChecked(false));


    if (shouldCheck) {
      this.store.dispatch(new NodeActions.UpdateNodesChecked(true, [node.id]));
      this.store.dispatch(new NodeActions.UpdateNodeSelected(node.id));
    }


    this.store.dispatch(new ExpandViewTreeNode(node));

    // Pre-request node info
    this.store.dispatch(new QueryNodeInfo(node));
  }

  hideNode(node: ViewTreeNode) {
    this.store.dispatch(new NodeActions.UpdateNodesShown(false, [node.id]));
    this.store.dispatch(new NodeActions.UpdateNodesChecked(false));
  }

  selectLabel(label: LabelModel, flyTo: boolean = false) {
    const { objectId, states } = label;

    // select label
    this.store.dispatch(new SelectLabel(label));

    // #TODO[AC]: fly to position
    // no custom actions: fly to object
    if (flyTo && objectId && states.length === 0) {
      this.store.dispatch(new FlyToObject(objectId, true));
    }
  }

  /**
   * On a new chapter change, immediately cache the current state so that we can revert.
   * While engine has a reset() method, it currently doesn't work well with painted objects.
   * @param chapter the ChapterModel to init
   */
  async initChapter(chapter: ChapterModel) {
    const viewGraph: BdGraph<ViewTreeNode> = this.engineService.createViewGraph(
      {},
      false
    );

    this.store.dispatch(new SetGraph(viewGraph));
    this.store.dispatch(new SetCamera(chapter.camera.location, chapter.camera.action === CameraStrategy.FlyTo));
  }

  playChapterFromStart() {
    this.store.dispatch(new PlayChapterFromStart());
  }

  resetCamera() {
    const chapter = this.store.selectSnapshot(StoryChaptersState.getSelectedChapter);
    if (!chapter) {
      return;
    }
    this.store.dispatch(new SetCamera(chapter.camera.location));
  }

  resetChapter() {
    this.resetCamera();

    const chapterId = this.store.selectSnapshot(
      StoryChaptersState.getSelectedChapterId
    );
    this.store.dispatch(new UndoActions.UndoAll(chapterId));

    this.store.dispatch(
      new NodeActions.UpdateNodesChecked(false)
    );
    this.store.dispatch(new RemoveContextLabel());
  }

  resetLayout() {
    const { ui: layout } = this.store.selectSnapshot(ViewState) as ViewStateModel;

    if (this.layoutService.isUIHidden()) {
      Object.keys(layout).forEach(key => {
        layout[key].status = ViewUIStatus.Hide;
      });
    } else {
      // Reset everything to visible
      Object.keys(layout).forEach(key => {
        layout[key].status = ViewUIStatus.Show;
      });

      // Always hidden by default
      layout.search.status = ViewUIStatus.Hide;
      layout.tools.status = ViewUIStatus.Hide;
      layout.nodeInfo.status = ViewUIStatus.Hide;
      layout.labelInfo.status = ViewUIStatus.Hide;


      layout.media.status = ViewUIStatus.Hide;
      layout.visibilityLayers.status = ViewUIStatus.Hide;
    }

    this.store.dispatch(new ViewActions.SetUI(layout));
  }

  undo() {
    this.store.dispatch(
      new UndoActions.Undo(
        this.store.selectSnapshot(StoryChaptersState.getSelectedChapterId)
      )
    );
  }

  redo() {
    this.store.dispatch(
      new UndoActions.Redo(
        this.store.selectSnapshot(StoryChaptersState.getSelectedChapterId)
      )
    );
  }

  toggleAnimation() {
    const playing = !this.store.selectSnapshot(EngineState.getAnimationPlaying);
    this.store.dispatch(new ToggleAnimation(playing));
  }

  toggleAudio() {
    const playAudio = this.store.selectSnapshot(EngineState.getPlayAudio);
    this.store.dispatch(new PlayAudio(!playAudio));
  }

  toggleToolbar(bar: 'media' | 'tools' | 'toolbar' | 'visibilityLayers') {
    const layout = this.store.selectSnapshot(ViewState.getUI);
    layout.media.status = ViewUIStatus.Hide;
    layout.tools.status = ViewUIStatus.Hide;
    layout.toolbar.status = ViewUIStatus.Hide;
    layout.visibilityLayers.status = ViewUIStatus.Hide;
    layout[bar].status = ViewUIStatus.Show;

    this.store.dispatch(new ViewActions.SetUI(layout));
  }


  normalizeQueryParams(queryParams: any): { id: string, type: ContentType, lang?: string } {
    // normalize query params
    const params = { ...queryParams };
    const contentId = normalizeQueryIdParam(params);
    const contentType = normalizeQueryTypeParam(params);
    const contentLang = queryParams.lang;

    return {
      id: contentId,
      type: <ContentType>contentType,
      lang: contentLang
    };
  }

  // Bookmark routes use appropriate ID parameters, but internal users would like the ability to paste
  // in legacy paths. This means stripping out the .json extension at time of page load and accounting
  // for that conversion later on.
  sanitizeRouteId(id: string): string {
    id = id.replace(/\.json$/, '');
    const query = `?id=${id}`;
    this.location.replaceState('view', query);
    return id;
  }

  openInNewTab() {
    const url = window.document.location.href;
    window.open(url);
  }

  // see: https://stackoverflow.com/questions/43065579/how-to-convert-css-transform-matrix-back-to-its-component-properties/43065744
  getComputedTransform(element: HTMLElement): { x: number, y: number } {
    const style = getComputedStyle(element);
    const trans = style.transform;


    // ie implementation difference.
    // ie returns a 3d matrix, while others return a 2d matrix.
    // sanitize transform string value
    const values = trans
      .replace('matrix3d', '')
      .replace('matrix', '')
      .replace('(', '')
      .replace(')', '')
      .split(',')
      .map(s => s.trim())
      .map(s => parseInt(s, 10));

    const computedTranslateX: number = values.length === 16 ? values[12] : values[4];
    const computedTranslateY: number = values.length === 16 ? values[13] : values[5];

    if (!Number.isNaN(computedTranslateX) && !Number.isNaN(computedTranslateY)) {
      return {
        x: computedTranslateX,
        y: computedTranslateY
      };
    } else {
      return null;
    }
  }

  getVH(includeOffset = true) {
    const siteHeader = this.store.selectSnapshot(LayoutState.getSiteHeader);
    const topOffset = !includeOffset || siteHeader === SiteHeaderType.Hide ? 0 : 56;

    // const siteHeader = document.querySelector('app-site-header');
    // const topOffset = siteHeader ? siteHeader.getBoundingClientRect().height : 0;
    
    const vh = (window.innerHeight - topOffset) * 0.01;
    return vh;
  }

  inferDrawerMode(layout: ViewUIModel) {
    // Default mode is content
    let mode = ViewMode.Content;

    if (layout.nodeInfo.status === ViewUIStatus.Show) {
      mode = ViewMode.NodeInfo;
    } else if (layout.labelInfo.status === ViewUIStatus.Show) {
      mode = ViewMode.LabelInfo;
    }

    return mode;
  }

  /**
   * Set UI according to configs in order of precedence:
   *
   * @param config UIConfig
   */
  setUI(config: {
    baseUIConfig?: UIConfig;
    apiUIConfig?: UIConfig;
    urlUIConfig?: UIConfig;
    chapterUIConfig?: UIConfig;
    quizUIConfig?: UIConfig;
  }) {
    let uiConfig = config.baseUIConfig ?? new UIConfig();
    
    uiConfig = WidgetWiring.mergeUIConfigs(uiConfig, config.apiUIConfig);
    uiConfig = WidgetWiring.mergeUIConfigs(uiConfig, config.urlUIConfig);
    uiConfig = WidgetWiring.mergeUIConfigs(uiConfig, config.chapterUIConfig);

    if (config.quizUIConfig) {
      // Quiz UI takes priority over everything else
      uiConfig = WidgetWiring.mergeUIConfigs(uiConfig, config.quizUIConfig, true); 
    }

    const viewUI = this.store.selectSnapshot(ViewState.getUI);
    const ui = WidgetWiring.createLayoutFromConfig(viewUI, uiConfig);
    
    this.store.dispatch(new ViewActions.SetUI(ui));
  }
}
